import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { informesAPI, fondosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';
import '../ProyeccionFondos.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatFechaCorta = (f) => {
  if (!f) return '';
  const s = String(f).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

const OPCIONES_INCLUIR = [
  { id: 'ingresos_clientes', label: 'Ingresos clientes', hint: 'Créditos en cuentas corrientes' },
  { id: 'egresos_proveedores', label: 'Egresos proveedores', hint: 'Débitos a proveedores' },
  { id: 'gastos', label: 'Gastos', hint: 'Gastos registrados' },
  { id: 'sueldos', label: 'Sueldos', hint: 'Pagos a empleados' },
  { id: 'movimientos_fondos', label: 'Movimientos fondos', hint: 'Neto ingresos/egresos de cajas y cuentas' },
];

const KPI_A_CONCEPTO = {
  ing: 'ingresos_clientes',
  egp: 'egresos_proveedores',
  gas: 'gastos',
  sue: 'sueldos',
  mf: 'movimientos_fondos',
  neto: 'resultado_neto',
};

function EntidadMultiselect({ label, items, selected, onToggle, onSelectAll, onClear, renderLabel }) {
  return (
    <div className="proyeccion-entidad-block">
      <label className="block-label">
        <span>{label}</span>
        <span className="block-actions">
          <button type="button" onClick={onSelectAll}>Todas</button>
          <button type="button" onClick={onClear}>Ninguna</button>
        </span>
      </label>
      <div className="proyeccion-multiselect">
        {items.length === 0 ? (
          <span style={{ color: '#999', fontSize: '12px' }}>Sin registros activos</span>
        ) : (
          items.map((item) => (
            <label key={item.id} className="proyeccion-multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(String(item.id))}
                onChange={() => onToggle(item.id)}
              />
              <span>{renderLabel(item)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function TablaDetalleMovimientos({ items, formatFecha }) {
  if (!items?.length) {
    return <p className="proyeccion-detalle-empty">No hay movimientos en este concepto para el período.</p>;
  }
  return (
    <div className="proyeccion-card__body proyeccion-card__body--table">
      <table className="data-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Entidad</th>
            <th>Concepto</th>
            <th>Tipo</th>
            <th style={{ textAlign: 'right' }}>Monto</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const signed = (parseFloat(row.monto) || 0) * (row.signo || 1);
            const neg = signed < 0;
            const pos = signed > 0 && row.signo === 1;
            return (
              <tr key={`${row.tipo || 'x'}-${row.id}`}>
                <td>{formatFecha(row.fecha)}</td>
                <td>{row.entidad || '—'}</td>
                <td>{row.concepto || '—'}</td>
                <td>{row.tipo || '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: neg ? '#c0392b' : pos ? '#27ae60' : undefined }}>
                  {formatMoney(row.monto)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ResultadosReales = () => {
  const { fechaHoy, formatFecha } = useZonaHoraria();
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [incluir, setIncluir] = useState(OPCIONES_INCLUIR.map((o) => o.id));
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [cajasSeleccionadas, setCajasSeleccionadas] = useState([]);
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState([]);
  const [entidadesCargadas, setEntidadesCargadas] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conceptoDetalle, setConceptoDetalle] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const detalleRef = useRef(null);
  const detalleSeqRef = useRef(0);

  useEffect(() => {
    const hoy = fechaHoy();
    setFechaDesde((prev) => prev || `${hoy.slice(0, 7)}-01`);
    setFechaHasta((prev) => prev || hoy);
  }, [fechaHoy]);

  useEffect(() => {
    fondosAPI.getEntidades({ activo: true })
      .then((res) => {
        if (!res.success) return;
        const entidades = res.data || [];
        const c = entidades.filter((e) => e.tipo_entidad === 'caja');
        const cb = entidades.filter((e) => e.tipo_entidad === 'cuenta');
        setCajas(c);
        setCuentas(cb);
        setCajasSeleccionadas(c.map((x) => String(x.id)));
        setCuentasSeleccionadas(cb.map((x) => String(x.id)));
        setEntidadesCargadas(true);
      })
      .catch(() => toast.error('No se pudieron cargar cajas y cuentas'));
  }, []);

  const paramsBase = useCallback(() => ({
    fecha_desde: fechaDesde,
    fecha_hasta: fechaHasta,
    incluir,
    caja_ids: cajasSeleccionadas,
    cuenta_bancaria_ids: cuentasSeleccionadas,
  }), [fechaDesde, fechaHasta, incluir, cajasSeleccionadas, cuentasSeleccionadas]);

  const cargar = useCallback(async () => {
    if (incluir.length === 0) {
      toast.warning('Seleccione al menos un concepto a incluir');
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      toast.warning('Indique fecha desde y hasta');
      return;
    }
    try {
      setLoading(true);
      setConceptoDetalle(null);
      setDetalle(null);
      const res = await informesAPI.resultadosReales(paramsBase());
      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.message || 'Error al calcular');
      }
    } catch (e) {
      toast.error(e.message || 'Error al calcular resultados');
    } finally {
      setLoading(false);
    }
  }, [incluir, fechaDesde, fechaHasta, paramsBase]);

  useEffect(() => {
    if (entidadesCargadas && fechaDesde && fechaHasta) cargar();
  }, [entidadesCargadas, fechaDesde, fechaHasta, cargar]);

  const cargarDetalle = useCallback(async (concepto) => {
    if (!fechaDesde || !fechaHasta) return;
    const seq = ++detalleSeqRef.current;
    setConceptoDetalle(concepto);
    setLoadingDetalle(true);
    try {
      const res = await informesAPI.resultadosRealesDetalle({
        ...paramsBase(),
        concepto,
      });
      if (seq !== detalleSeqRef.current) return;
      if (res.success) {
        setDetalle(res.data);
        requestAnimationFrame(() => {
          detalleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      } else {
        toast.error(res.message || 'Error al cargar detalle');
        setDetalle(null);
      }
    } catch (e) {
      if (seq !== detalleSeqRef.current) return;
      toast.error(e.message || 'Error al cargar detalle');
      setDetalle(null);
    } finally {
      if (seq === detalleSeqRef.current) setLoadingDetalle(false);
    }
  }, [fechaDesde, fechaHasta, paramsBase]);

  const toggleIncluir = (id) => {
    setIncluir((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleId = (id, setter) => {
    const s = String(id);
    setter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const fmtFecha = useCallback(
    (f) => {
      try {
        return formatFecha ? formatFecha(f) : formatFechaCorta(f);
      } catch (_) {
        return formatFechaCorta(f);
      }
    },
    [formatFecha]
  );

  const kpis = useMemo(() => {
    if (!data?.totales) return [];
    const t = data.totales;
    const items = [];
    if (incluir.includes('ingresos_clientes')) {
      items.push({ key: 'ing', label: 'Ingresos clientes', value: t.ingresos_clientes, positive: true });
    }
    if (incluir.includes('egresos_proveedores')) {
      items.push({ key: 'egp', label: 'Egresos proveedores', value: t.egresos_proveedores, negative: true });
    }
    if (incluir.includes('gastos')) {
      items.push({ key: 'gas', label: 'Gastos', value: t.gastos, negative: true });
    }
    if (incluir.includes('sueldos')) {
      items.push({ key: 'sue', label: 'Sueldos pagados', value: t.sueldos, negative: true });
    }
    if (incluir.includes('movimientos_fondos')) {
      items.push({ key: 'mf', label: 'Neto mov. fondos', value: t.movimientos_fondos });
    }
    items.push({
      key: 'neto',
      label: 'Resultado neto',
      value: t.resultado_neto,
      highlight: true,
      negative: (t.resultado_neto || 0) < 0,
      positive: (t.resultado_neto || 0) > 0,
    });
    return items;
  }, [data, incluir]);

  return (
    <div className="clientes proyeccion-fondos">
      <div className="page-header">
        <h1>Resultados reales</h1>
        <p className="page-subtitle">Totales históricos del período — no es proyección. Clic en un total para ver los movimientos.</p>
      </div>

      <div className="proyeccion-layout">
        <aside className="proyeccion-panel">
          <div className="proyeccion-panel__head">
            <h2>Parámetros</h2>
          </div>
          <div className="proyeccion-panel__body">
            <div className="proyeccion-toolbar">
              <div className="proyeccion-field">
                <label htmlFor="rr-desde">Desde</label>
                <input
                  id="rr-desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="proyeccion-field">
                <label htmlFor="rr-hasta">Hasta</label>
                <input
                  id="rr-hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
              <button type="button" className="btn-primary" onClick={cargar} disabled={loading}>
                {loading ? '…' : 'Recalcular'}
              </button>
            </div>

            <div className="proyeccion-section">
              <p className="proyeccion-section__title">Conceptos</p>
              <div className="proyeccion-chips">
                {OPCIONES_INCLUIR.map((op) => {
                  const active = incluir.includes(op.id);
                  return (
                    <label
                      key={op.id}
                      className={`proyeccion-chip${active ? ' proyeccion-chip--active' : ''}`}
                    >
                      <input type="checkbox" checked={active} onChange={() => toggleIncluir(op.id)} />
                      <span>
                        <strong>{op.label}</strong>
                        <br />
                        <span style={{ color: '#888' }}>{op.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {incluir.includes('movimientos_fondos') && (
              <div className="proyeccion-section">
                <p className="proyeccion-section__title">Fondos (movimientos)</p>
                <div className="proyeccion-entidades-grid">
                  <EntidadMultiselect
                    label="Cajas"
                    items={cajas}
                    selected={cajasSeleccionadas}
                    onToggle={(cid) => toggleId(cid, setCajasSeleccionadas)}
                    onSelectAll={() => setCajasSeleccionadas(cajas.map((c) => String(c.id)))}
                    onClear={() => setCajasSeleccionadas([])}
                    renderLabel={(c) => c.nombre}
                  />
                  <EntidadMultiselect
                    label="Cuentas"
                    items={cuentas}
                    selected={cuentasSeleccionadas}
                    onToggle={(cid) => toggleId(cid, setCuentasSeleccionadas)}
                    onSelectAll={() => setCuentasSeleccionadas(cuentas.map((c) => String(c.id)))}
                    onClear={() => setCuentasSeleccionadas([])}
                    renderLabel={(cb) => `${cb.banco_nombre || 'Banco'} — ${cb.numero_cuenta || cb.nombre}`}
                  />
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className="proyeccion-main">
          {loading && !data && (
            <div className="proyeccion-loading">Calculando resultados…</div>
          )}

          {data && (
            <>
              <div className="proyeccion-kpis">
                {kpis.map((k) => {
                  const concepto = KPI_A_CONCEPTO[k.key];
                  const active = conceptoDetalle === concepto;
                  return (
                    <button
                      type="button"
                      key={k.key}
                      className={`proyeccion-kpi proyeccion-kpi--clickable${k.highlight ? ' proyeccion-kpi--highlight' : ''}${k.negative ? ' proyeccion-kpi--negative' : ''}${active ? ' proyeccion-kpi--active' : ''}`}
                      onClick={() => cargarDetalle(concepto)}
                      title="Ver movimientos que forman este total"
                    >
                      <span className="proyeccion-kpi__label">{k.label}</span>
                      <span className={`proyeccion-kpi__value${k.positive ? ' positive' : ''}${k.negative ? ' negative' : ''}`}>
                        {formatMoney(k.value)}
                      </span>
                      <span className="proyeccion-kpi__hint">Ver detalle</span>
                    </button>
                  );
                })}
              </div>

              {(conceptoDetalle || loadingDetalle) && (
                <div className="proyeccion-card proyeccion-detalle" ref={detalleRef}>
                  <div className="proyeccion-card__head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span>
                      {loadingDetalle
                        ? 'Cargando movimientos…'
                        : `Movimientos — ${detalle?.label || ''}`}
                      {!loadingDetalle && detalle && (
                        <span style={{ marginLeft: 10, fontWeight: 400, color: '#64748b' }}>
                          ({detalle.fecha_desde || '…'} a {detalle.fecha_hasta || '…'}) · Total {formatMoney(detalle.total)}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        detalleSeqRef.current += 1;
                        setConceptoDetalle(null);
                        setDetalle(null);
                        setLoadingDetalle(false);
                      }}
                    >
                      Cerrar
                    </button>
                  </div>
                  {loadingDetalle && (
                    <div className="proyeccion-loading" style={{ padding: 24 }}>Cargando…</div>
                  )}
                  {!loadingDetalle && detalle?.secciones?.map((sec) => (
                    <div key={sec.concepto} className="proyeccion-detalle-seccion">
                      {detalle.secciones.length > 1 && (
                        <div className="proyeccion-detalle-seccion__head">
                          <strong>{sec.label}</strong>
                          <span>{formatMoney(sec.total)} · {sec.items?.length || 0} ítems</span>
                        </div>
                      )}
                      <TablaDetalleMovimientos items={sec.items} formatFecha={fmtFecha} />
                    </div>
                  ))}
                </div>
              )}

              <div className="proyeccion-card">
                <div className="proyeccion-card__head">Resumen del período</div>
                <div className="proyeccion-card__body proyeccion-card__body--table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Período</th>
                        {incluir.includes('ingresos_clientes') && <th>Ingresos clientes</th>}
                        {incluir.includes('egresos_proveedores') && <th>Egresos proveedores</th>}
                        {incluir.includes('gastos') && <th>Gastos</th>}
                        {incluir.includes('sueldos') && <th>Sueldos</th>}
                        {incluir.includes('movimientos_fondos') && <th>Neto fondos</th>}
                        <th>Resultado neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.series || []).map((row) => (
                        <tr key={row.periodo}>
                          <td><strong>{row.periodo}</strong></td>
                          {incluir.includes('ingresos_clientes') && (
                            <td style={{ color: '#27ae60' }}>{formatMoney(row.ingresos_clientes)}</td>
                          )}
                          {incluir.includes('egresos_proveedores') && (
                            <td style={{ color: '#c0392b' }}>{formatMoney(row.egresos_proveedores)}</td>
                          )}
                          {incluir.includes('gastos') && (
                            <td style={{ color: '#c0392b' }}>{formatMoney(row.gastos)}</td>
                          )}
                          {incluir.includes('sueldos') && (
                            <td style={{ color: '#c0392b' }}>{formatMoney(row.sueldos)}</td>
                          )}
                          {incluir.includes('movimientos_fondos') && (
                            <td>{formatMoney(row.movimientos_fondos)}</td>
                          )}
                          <td style={{ fontWeight: 600, color: (row.resultado_neto || 0) < 0 ? '#e74c3c' : '#2c3e50' }}>
                            {formatMoney(row.resultado_neto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!loading && !data && entidadesCargadas && (
            <div className="proyeccion-empty">Configure el período y pulse Recalcular</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultadosReales;
